import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { TypedContext } from 'src/app/controls/advanced-gridview/models/typed-context';
import { AdvancedService } from 'src/app/controls/advanced-gridview/services/advanced-service';
import { CrudFormComponent } from 'src/app/controls/crud-form/crud-form.component';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { ChannelProcess } from 'src/app/models/channelProcess';
import { Customer } from 'src/app/models/customer';
import { ProcessType } from 'src/app/models/enums/process-type';
import { Rights } from 'src/app/models/enums/rights';
import { IChannel } from 'src/app/models/interfaces/channel.interface';
import { ProcessDefinition } from 'src/app/models/process-definition';
import { ChannelProcessService } from 'src/app/services/channel-process.service';
import { CustomersService } from 'src/app/services/customers.service';
import { DocumentTypesService } from 'src/app/services/document-type.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ProcessDefinitionService } from 'src/app/services/process-definition.service';
import { AdvancedGridviewComponent } from '../../../../controls/advanced-gridview/advanced-gridview.component';
import { AttributesAssignmentFormDialogComponent } from './attributes-assignment-form-dialog/attributes-assignment-form-dialog.component';

@Component({
    selector: 'app-attributes-assignment',
    templateUrl: '../../../../controls/crud-form/crud-form.component.html',
    styleUrls: ['../../../../controls/crud-form/crud-form.component.scss'],
    imports: [AdvancedGridviewComponent, MatButtonModule, FileUploadModule, NgxSpinnerModule]
})
export class AttributesAssignmentComponent extends CrudFormComponent<ChannelProcess, IChannel, AttributesAssignmentFormDialogComponent> {
    public constructor(
        private customersService: CustomersService,
        public documentTypesService: DocumentTypesService,
        public processDefinitionService: ProcessDefinitionService,
        private channelProcessService: ChannelProcessService,
        public override enumHelper: EnumHelper,
        protected override notificationService: NotificationService,
        protected override spinner: NgxSpinnerService,
        public override dialog: MatDialog,
        public override memoryService: MemoryService
    ) {
        super(enumHelper, notificationService, spinner, dialog, memoryService);
    }

    /**
     * Définition des colonnes de la Gridview.
     */
    override get defaultTableConfig(): ColumnDefinition[] {
        return [
            new ColumnListDefinition<ProcessDefinition>('processDefinitionId', 'Processus', FilterType.List, 2, 30, true, false),
            new ColumnListDefinition<Customer>('customerId', 'Client', FilterType.List, 3, 30, true, false),
            new ColumnListDefinition<DocumentType>('documentTypeId', 'Type de document', FilterType.List, 4, 15, true, false),
            new ColumnDefinition('issuer', 'Emetteur', ColumnType.Text, FilterType.Text, 4, 20, true),
        ];
    }

    /**
     * Définition des colonnes de la Gridview.
     */
    override get withIdTableConfig(): ColumnDefinition[] {
        return [
            new ColumnDefinition('id', 'Id', ColumnType.Text, FilterType.Text, 1, 5, true),
            new ColumnListDefinition<ProcessDefinition>('processDefinitionId', 'Processus', FilterType.List, 2, 30, true, false),
            new ColumnListDefinition<Customer>('customerId', 'Client', FilterType.List, 3, 25, true, false),
            new ColumnListDefinition<DocumentType>('documentTypeId', 'Type de document', FilterType.List, 4, 15, true, false),
            new ColumnDefinition('issuer', 'Emetteur', ColumnType.Text, FilterType.Text, 4, 20, true),
        ];
    }

    /**
     * Charge les éléments des filtres d'une colonnes de type liste.
     * @param datasourceContext
     * @param columns
     */
    protected override loadListColumnDatas(datasourceContext: Context, columns: ColumnListDefinition<string>[]): void {
        this.loadCustomers(columns);
        this.loadDocumentTypes(columns);
        this.loadProcessDefinitions(columns);
    }

    private loadCustomers(columns: ColumnListDefinition<string>[]): void {
        this.customersService.List(null, null, null, null).subscribe((x) => {
            var listItem = x.value.map<ListItem<string>>(
                (v) =>
                    <ListItem<string>>{
                        value: v.id.toString(),
                        text: v.name,
                    }
            );
            this.setColumnFilterDatas(
                columns.find((x) => x.columnName === 'customerId'),
                listItem
            );
        });
    }

    private loadDocumentTypes(columns: ColumnListDefinition<string>[]): void {
        this.documentTypesService.List(null, null, null, null).subscribe((x) => {
            var listItem = x.value.map<ListItem<string>>(
                (v) =>
                    <ListItem<string>>{
                        value: v.id.toString(),
                        text: v.name,
                    }
            );
            this.setColumnFilterDatas(
                columns.find((x) => x.columnName === 'documentTypeId'),
                listItem
            );
        });
    }

    private loadProcessDefinitions(columns: ColumnListDefinition<string>[]): void {
        const definitionContext: Context = new Context('type', ProcessType.AttributesAssignment.toString());
        this.processDefinitionService.List(definitionContext, null, null, null).subscribe((x) => {
            var listItem = x.value.map<ListItem<string>>(
                (v) =>
                    <ListItem<string>>{
                        value: v.id.toString(),
                        text: v.name,
                    }
            );
            this.setColumnFilterDatas(
                columns.find((x) => x.columnName === 'processDefinitionId'),
                listItem
            );
        });
    }

    /**
     * Instancie un nouvel enregistrement.
     */
    protected override instantiateNewRecord(): ChannelProcess {
        let record = new ChannelProcess();
        record.receivingChannelId = this._context.item.id;
        return record;
    }

    /**
     * Retourne le texte de confirmation de la suppression.
     */
    protected override getDeleteConfirmationText(item: ChannelProcess): string {
        return `Vous êtes sur le point de supprimer l\'assignation d\'attributs '${item.id}'.`;
    }

    /**
     * Retourne le texte de suppression.
     */
    protected override getDeletionText(): string {
        return "processus d'assignation d'attributs supprimé.";
    }

    /**
     * Retourne le texte d'erreur de suppression.
     */
    protected override getDeletionErrorText(): string {
        return "Impossible de supprimer le processus  d'assignation d'attributs.";
    }

    /**
     * Retourne le texte de création.
     */
    protected override getCreationText(): string {
        return "Processus d'assignation d'attributs assigné.";
    }

    /**
     * Retourne le texte d'erreur de création.
     */
    protected override getCreationErrorText(): string {
        return "Impossible d'assigner un processus d'assignation d'attributs.";
    }

    /**
     * Retourne le texte de mise à jour.
     */
    protected override getUpdateText(): string {
        return "processus d'assignation d'attributs mis à jour";
    }

    /**
     * Retourne le texte d'erreur de mise à jour.
     */
    protected override getUpdateErrorText(): string {
        return "Impossible de mettre à jour le processus d'assignation d'attributs.";
    }

    /**
     * Retourne la requête pour supprimer un enregistrement.
     * @param item : Instance de l'enregistrement à supprimer.
     */
    protected override getDeletionRequest(item: ChannelProcess): Observable<boolean> {
        return this.channelProcessService.delete(item.receivingChannelId, item.id);
    }

    /**
     * Retourne la requête pour créer un enregistrement.
     * @param item : Instance de l'enregistrement à créer.
     */
    protected override getCreationRequest(item: ChannelProcess): Observable<ChannelProcess> {
        return this.channelProcessService.add(item.receivingChannelId, item);
    }

    /**
     * Retourne la requête pour mettre à jour un enregistrement.
     * @param item : Instance de l'enregistrement à mettre à jour.
     */
    protected override getUpdateRequest(item: ChannelProcess): Observable<ChannelProcess> {
        return this.channelProcessService.update(item.receivingChannelId, item.id, item);
    }

    /**
     * Retourne la requête pour uploader le fichier d'import.
     */
    protected override getUploadRequest(): string {
        return `${this.baseUrl}/api/organizations/${this.organizationId}/views/${this.viewId}/channels/${this._context.item.id}/processes/import`;
    }

    /**
     * Retourne le composant boite de dialogue utilisé pour afficher le formulaire de création / édition / consultation.
     */
    protected override getDialogComponent(): new (...args: any[]) => AttributesAssignmentFormDialogComponent {
        return AttributesAssignmentFormDialogComponent;
    }

    /**
     * Retourne le nom du fichier d'export.
     */
    protected override getExportFileName(): string {
        return `Processus d\'assignation d\'attributs du canal ${this._context.item.id}.xlsx`;
    }

    /**
     * Retourne le duplicata d'un enregistrement.
     * @param item : Instance de l'enregistrement à dupliquer.
     */
    protected override duplicate(item: ChannelProcess): ChannelProcess {
        let record = new ChannelProcess();
        record.documentTypeId = item.documentTypeId;
        record.customerId = item.customerId;
        record.issuer = item.issuer;
        record.processDefinitionId = item.processDefinitionId;
        record.receivingChannelId = item.receivingChannelId;
        return record;
    }

    /**
     * Retourne la source de données de la liste.
     */
    protected override getDatasourceService(): AdvancedService<ChannelProcess> {
        return this.channelProcessService;
    }

    /**
     * Retourne le contexte utilisé par la source de données de la liste.
     */
    protected override getDatasourceContext(): Context {
        return {
            property: 'channelId',
            value: this._context.item.id,
            type: 'AttributesAssignment',
        } as TypedContext;
    }

    /**
     * Retourne les infos de tri par défaut de la liste.
     */
    protected override getDefaultSortInfos(): SortInfos {
        return new SortInfos('processDefinitionId', 'asc');
    }

    /**
     * Retourne le droit necessaire pour pouvoir crééer un enregistrement.
     */
    public override getCreationRight(): Rights {
        return Rights.ProcessCreate;
    }

    /**
     * Retourne le droit necessaire pour pouvoir mettre à jour un enregistrement.
     */
    protected override getUpdateRight(): Rights {
        return Rights.ProcessEdit;
    }

    /**
     * Retourne le droit necessaire pour pouvoir supprimer un enregistrement.
     */
    protected override getDeletionRight(): Rights {
        return Rights.ProcessDelete;
    }
}
